/**
 * The desktop game layout: the Stage column and the Rail, side by side and
 * CENTRED in the page.
 *
 * A dumb slot placer (unit convention §4): no store reads, no selectors, no
 * theme — only the elements it is handed and the geometry it is told. That is
 * what lets `Body` stay out of the way of a socket message: the elements are
 * created in `Body`, which does not re-render on a move.
 */
import React from 'react';
import {StyleSheet, View} from 'react-native';

import {LAYOUT} from '../theme/useV2Ui';

export type DesktopLayoutProps = {
  stage: React.ReactNode;
  /** a SIBLING of the stage, so its dim covers the board column only */
  overlay: React.ReactNode;
  rail: React.ReactNode;
  railWidth: number;
};

export default React.memo(function DesktopLayout({
  stage,
  overlay,
  rail,
  railWidth,
}: DesktopLayoutProps) {
  return (
    <View style={styles.row}>
      <View style={styles.boardColumn}>
        {stage}
        {overlay}
      </View>
      <View style={[styles.railColumn, {width: railWidth}]}>{rail}</View>
    </View>
  );
});

const styles = StyleSheet.create({
  row: {
    flex: 1,
    flexDirection: 'row',
    // M1b feedback item 1: the pair is CENTRED, so a wide viewport splits the
    // slack evenly instead of leaving it all to the right of the rail. The
    // board size formula (`useV2Ui.computeDesktopBoardSize`) already subtracts
    // the rail, the gap and this padding, so nothing else moves.
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: LAYOUT.stageRailGap,
    paddingHorizontal: LAYOUT.pagePadding,
    // owner item 2 of the 2026-10-05 clean-up: half the horizontal padding
    // above and below, so the height-bound board gets those pixels
    paddingVertical: LAYOUT.pagePaddingVertical,
  },
  boardColumn: {
    position: 'relative',
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  railColumn: {
    // a FIXED column (useV2Ui.railWidth): 400 clamped to [360, 440] normally,
    // 480 clamped to [400, 540] in the wide tournament/analysis mode (fix D).
    // The board takes everything else, instead of the two splitting the row
    // 50/50 — so the clamp here has to allow the WIDE maximum or the rail's
    // own width prop would be capped back to 440.
    flexGrow: 0,
    flexShrink: 0,
    minWidth: LAYOUT.railMin,
    maxWidth: LAYOUT.railWideMax,
    alignSelf: 'stretch',
  },
});
